import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { useAccounts } from '../../../context/AccountContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { RefreshCw, Loader2, AlertCircle, Trophy, Play, Image as ImageIcon, Monitor, ArrowLeftRight } from 'lucide-react';
import { isDemoBrand, demoHash } from '../../../utils/demoBrand';

const API_URL = 'https://api.glowytics.com';

// ── Demo-brand static A/B data ──────────────────────────────────────────────
const DEMO_AB_CAMPAIGNS = [
  'ADV+ Alışveriş | Prospecting',
  'Retargeting | 30G Site',
  'Lookalike %3 | Satın Alma',
  'Reels | Marka Bilinirliği',
  'Broad | Interest Stack',
  'DPA | Katalog Retargeting',
];

function buildDemoCampaignList() {
  return DEMO_AB_CAMPAIGNS.map((name, i) => ({
    campaign_id: `demo-ab-${i}`,
    campaign_name: name,
    spend: 30000 + (demoHash(name) % 90000),
  }));
}

// Full metrics + daily series + creative preview for one campaign, keyed off its name.
function buildDemoABSide(name) {
  const h = demoHash(name);
  const spend = 30000 + (h % 90000);
  const roas = 2.1 + (h % 160) / 100;               // 2.1 – 3.7x
  const revenue = Math.round(spend * roas);
  const impressions = spend * (28 + (h % 22));
  const clicks = Math.round(impressions * (0.008 + (h % 14) / 1000));
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const cpc = clicks > 0 ? spend / clicks : 0;
  const cpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
  const conversions = Math.round(clicks * (0.03 + (h % 25) / 1000));
  const cpa = conversions > 0 ? spend / conversions : 0;
  const cvr = clicks > 0 ? (conversions / clicks) * 100 : 0;
  const reach = Math.round(impressions / (1.5 + (h % 10) / 10));
  const frequency = reach > 0 ? impressions / reach : 0;

  // 30-day daily ROAS/spend series
  const daily = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const dh = demoHash(`${name}-${i}`);
    const daySpend = Math.round((spend / 30) * (0.7 + (dh % 60) / 100));
    const dayRoas = Number((roas * (0.75 + (dh % 50) / 100)).toFixed(2));
    daily.push({ date: d.toISOString().split('T')[0], spend: daySpend, roas: dayRoas });
  }

  const ads = Array.from({ length: 3 }).map((_, ai) => ({
    ad_id: `demo-${h}-${ai}`,
    ad_name: `${name.split('|')[0].trim()} — Creative ${ai + 1}`,
    image_url: `https://picsum.photos/seed/gwab${h % 1000}${ai}/600/600`,
    is_video: false,
    status: 'ACTIVE',
  }));

  return {
    found: true,
    campaign_name: name,
    spend, revenue, roas, conversions, cpa, cvr,
    impressions, clicks, ctr, cpc, cpm, reach, frequency,
    daily,
    preview: { found: true, ads },
  };
}

function buildDemoABCompare(aName, bName) {
  return {
    A: buildDemoABSide(aName || DEMO_AB_CAMPAIGNS[0]),
    B: buildDemoABSide(bName || DEMO_AB_CAMPAIGNS[1]),
    accountId: 'act_demo',
    window: { since: '—', until: '—' },
  };
}

const DATE_RANGES = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'all', label: 'All time' },
];

const fmtInt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtMoney = (n) => '₺' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney0 = (n) => '₺' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => fmtNum(n, 2) + '%';

// metric definition: key, label, formatter, "higher is better?"
const METRICS = [
  { key: 'spend', label: 'Spend', fmt: fmtMoney, higher: false },
  { key: 'revenue', label: 'Revenue', fmt: fmtMoney, higher: true },
  { key: 'roas', label: 'ROAS', fmt: (v) => fmtNum(v, 2) + 'x', higher: true },
  { key: 'conversions', label: 'Conversions (purchases)', fmt: fmtInt, higher: true },
  { key: 'cpa', label: 'CPA', fmt: fmtMoney, higher: false },
  { key: 'cvr', label: 'CVR (click→conv.)', fmt: fmtPct, higher: true },
  { key: 'impressions', label: 'Impressions', fmt: fmtInt, higher: true },
  { key: 'clicks', label: 'Clicks', fmt: fmtInt, higher: true },
  { key: 'ctr', label: 'CTR', fmt: fmtPct, higher: true },
  { key: 'cpc', label: 'CPC', fmt: fmtMoney, higher: false },
  { key: 'cpm', label: 'CPM', fmt: fmtMoney, higher: false },
  { key: 'reach', label: 'Reach', fmt: fmtInt, higher: true },
  { key: 'frequency', label: 'Frequency', fmt: (v) => fmtNum(v, 2), higher: false },
];

export default function MetaABCompare() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const accParam = selectedAccountId && selectedAccountId !== 'all' ? selectedAccountId : undefined;

  const [dateRange, setDateRange] = useState('last30');
  const [campaigns, setCampaigns] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [campaignA, setCampaignA] = useState('');
  const [campaignB, setCampaignB] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // ── Comparison (live request) ──
  const compare = useCallback(async (aName, bName) => {
    if (!selectedBrand?.id || !aName || !bName) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/meta-ads/live/ab-compare`, {
        params: { campaignA: aName, campaignB: bName, dateRange, ...(accParam ? { accountId: accParam } : {}) },
        headers: { 'X-Brand-ID': selectedBrand.id },
        withCredentials: true,
        timeout: 120000,
      });
      const hasData = res.data?.A?.found || res.data?.B?.found;
      setData((isDemoBrand(selectedBrand) && !hasData) ? buildDemoABCompare(aName, bName) : res.data);
    } catch (e) {
      if (isDemoBrand(selectedBrand)) { setData(buildDemoABCompare(aName, bName)); setError(null); }
      else setError(e?.response?.data?.error || e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, dateRange, accParam]);

  // ── Load campaign list live (populate dropdowns) ──
  const loadList = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setListLoading(true); setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/meta-ads/live/campaign-insights`, {
        params: { dateRange, ...(accParam ? { accountId: accParam } : {}) },
        headers: { 'X-Brand-ID': selectedBrand.id },
        withCredentials: true,
        timeout: 120000,
      });
      let list = (r.data?.campaigns || []).filter((c) => c.campaign_name);
      if (list.length === 0 && isDemoBrand(selectedBrand)) list = buildDemoCampaignList();
      setCampaigns(list);
      // If current selection isn't in the list, auto-pick the two highest-spending campaigns
      const names = new Set(list.map((c) => c.campaign_name));
      let a = names.has(campaignA) ? campaignA : (list[0]?.campaign_name || '');
      let b = names.has(campaignB) && campaignB !== a ? campaignB : (list.find((c) => c.campaign_name !== a)?.campaign_name || '');
      setCampaignA(a); setCampaignB(b);
      if (a && b) compare(a, b);
      else setData(null);
    } catch (e) {
      if (isDemoBrand(selectedBrand)) {
        const list = buildDemoCampaignList();
        setCampaigns(list);
        const a = list[0].campaign_name, b = list[1].campaign_name;
        setCampaignA(a); setCampaignB(b);
        compare(a, b);
        setError(null);
      } else {
        setError(e?.response?.data?.error || e.message || 'Failed to load campaign list');
      }
    } finally {
      setListLoading(false);
    }
  }, [selectedBrand, dateRange, accParam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(); }, [loadList]);

  const pick = (which, value) => {
    let a = campaignA, b = campaignB;
    if (which === 'A') { a = value; setCampaignA(value); } else { b = value; setCampaignB(value); }
    if (a && b) compare(a, b);
  };

  const swap = () => {
    const a = campaignB, b = campaignA;
    setCampaignA(a); setCampaignB(b);
    if (a && b) compare(a, b);
  };

  const A = data?.A;
  const B = data?.B;

  const chartData = useMemo(() => {
    if (!A?.daily && !B?.daily) return [];
    const map = {};
    (A?.daily || []).forEach((d) => { map[d.date] = { ...(map[d.date] || { date: d.date }), roasA: d.roas, spendA: d.spend }; });
    (B?.daily || []).forEach((d) => { map[d.date] = { ...(map[d.date] || { date: d.date }), roasB: d.roas, spendB: d.spend }; });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [A, B]);

  const winnerOf = (m) => {
    if (!A || !B) return null;
    const va = Number(A[m.key] || 0), vb = Number(B[m.key] || 0);
    if (va === vb) return null;
    const aWins = m.higher ? va > vb : va < vb;
    return aWins ? 'A' : 'B';
  };

  const overall = useMemo(() => {
    if (!A || !B) return null;
    if ((A.roas || 0) !== (B.roas || 0)) return (A.roas || 0) > (B.roas || 0) ? 'A' : 'B';
    if ((A.cpa || 0) !== (B.cpa || 0)) return (A.cpa || 0) < (B.cpa || 0) ? 'A' : 'B';
    return null;
  }, [A, B]);

  const cellCls = (side, m) => (winnerOf(m) === side ? 'font-bold text-emerald-700 bg-emerald-50/60' : 'text-gray-800');

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 border border-blue-100 shadow-sm rounded-xl">
            <img src="/6033716.png" alt="Meta Ads" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">A/B Test — Campaign Comparison</h1>
            <p className="text-sm text-gray-500 mt-1">Pick two campaigns and compare them side by side with live Meta data</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <AccountSwitcher platform="META_ADS" />
          <select
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* Campaign picker */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-end gap-3">
          <div className="flex-1">
            <label className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 mb-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500" /> Campaign A
            </label>
            <select
              value={campaignA}
              onChange={(e) => pick('A', e.target.value)}
              disabled={listLoading || campaigns.length === 0}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:ring-2 focus:ring-blue-100 outline-none hover:border-gray-300 disabled:bg-gray-50"
            >
              {campaigns.length === 0 && <option value="">{listLoading ? 'Loading…' : 'No campaigns'}</option>}
              {campaigns.map((c) => (
                <option key={c.campaign_id || c.campaign_name} value={c.campaign_name}>
                  {c.campaign_name} · {fmtMoney0(c.spend)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={swap}
            title="Swap A ↔ B"
            className="self-center mt-1 lg:mt-0 p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftRight className="w-4 h-4" />
          </button>

          <div className="flex-1">
            <label className="flex items-center gap-1.5 text-xs font-semibold text-orange-700 mb-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500" /> Campaign B
            </label>
            <select
              value={campaignB}
              onChange={(e) => pick('B', e.target.value)}
              disabled={listLoading || campaigns.length === 0}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:ring-2 focus:ring-orange-100 outline-none hover:border-gray-300 disabled:bg-gray-50"
            >
              {campaigns.length === 0 && <option value="">{listLoading ? 'Loading…' : 'No campaigns'}</option>}
              {campaigns.map((c) => (
                <option key={c.campaign_id || c.campaign_name} value={c.campaign_name}>
                  {c.campaign_name} · {fmtMoney0(c.spend)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => compare(campaignA, campaignB)}
            disabled={loading || !campaignA || !campaignB}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {loading ? 'Loading…' : 'Compare'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {listLoading ? 'Loading campaigns…' : `${campaigns.length} campaigns · top two by spend auto-selected`}
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Fetching live data…
        </div>
      )}

      {data && (
        <>
          {(!A?.found || !B?.found) && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>One of the campaigns has no data in the selected range. Try widening the date range.</span>
            </div>
          )}

          {/* Header cards */}
          <div className="grid grid-cols-2 gap-4">
            <SideHeader side="A" name={A?.campaign_name} color="blue" winner={overall === 'A'} />
            <SideHeader side="B" name={B?.campaign_name} color="orange" winner={overall === 'B'} />
          </div>

          {/* Creative previews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CampaignPreview side="A" color="blue" preview={A?.preview} />
            <CampaignPreview side="B" color="orange" preview={B?.preview} />
          </div>

          {/* Metrics table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Metrik</th>
                  <th className="text-right px-4 py-3 font-semibold text-blue-700">A</th>
                  <th className="text-right px-4 py-3 font-semibold text-orange-700">B</th>
                  <th className="text-right px-4 py-3 font-semibold">Diff (B−A)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {METRICS.map((m) => {
                  const va = Number(A?.[m.key] || 0), vb = Number(B?.[m.key] || 0);
                  const diff = vb - va;
                  const diffPct = va !== 0 ? (diff / va) * 100 : null;
                  const goodForB = diff !== 0 && (m.higher ? diff > 0 : diff < 0);
                  return (
                    <tr key={m.key} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-600">{m.label}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${cellCls('A', m)}`}>{m.fmt(va)}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${cellCls('B', m)}`}>{m.fmt(vb)}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${diff === 0 ? 'text-gray-400' : goodForB ? 'text-emerald-600' : 'text-red-600'}`}>
                        {diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${m.fmt(diff)}${diffPct !== null ? ` (${diffPct > 0 ? '+' : ''}${fmtNum(diffPct, 1)}%)` : ''}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Daily trend chart */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily ROAS trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsLineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => (v == null ? '—' : `${fmtNum(v, 2)}x`)} />
                  <Legend />
                  <Line type="monotone" dataKey="roasA" name="A" stroke="#2563eb" dot={false} strokeWidth={2} connectNulls />
                  <Line type="monotone" dataKey="roasB" name="B" stroke="#ea580c" dot={false} strokeWidth={2} connectNulls />
                </RechartsLineChart>
              </ResponsiveContainer>
            </div>
          )}

          <p className="text-xs text-gray-400">
            Source: Meta Graph API (live) · Account {data.accountId} · {data.window?.since} → {data.window?.until}
          </p>
        </>
      )}
    </div>
  );
}

function CampaignPreview({ side, color, preview }) {
  const ads = preview?.ads || [];
  const [activeIdx, setActiveIdx] = useState(0);
  const [mode, setMode] = useState('media'); // 'media' | 'iframe'
  const accent = color === 'blue' ? 'text-blue-700' : 'text-orange-700';

  if (!preview || !preview.found || ads.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <span className={`text-xs font-bold uppercase ${accent}`}>Preview {side}</span>
        <div className="h-48 flex items-center justify-center text-sm text-gray-400">No creative found</div>
      </div>
    );
  }

  const active = ads[Math.min(activeIdx, ads.length - 1)];

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-bold uppercase ${accent}`}>Preview {side}</span>
        {preview.preview_iframe && (
          <button
            onClick={() => setMode((m) => (m === 'iframe' ? 'media' : 'iframe'))}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-2 py-0.5"
          >
            <Monitor className="w-3 h-3" /> {mode === 'iframe' ? 'Image' : 'Rendered preview'}
          </button>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: 320 }}>
        {mode === 'iframe' && preview.preview_iframe ? (
          <iframe title={`preview-${side}`} src={preview.preview_iframe} className="w-full" style={{ height: 540, border: 'none' }} scrolling="no" />
        ) : active.is_video && active.video_url ? (
          <video key={active.ad_id} src={active.video_url} poster={active.image_url || undefined} controls className="w-full max-h-[420px] object-contain bg-black" />
        ) : active.image_url ? (
          <img src={active.image_url} alt={active.ad_name || ''} className="w-full max-h-[420px] object-contain" />
        ) : (
          <div className="h-48 flex items-center justify-center text-sm text-gray-400">No image</div>
        )}
      </div>

      <div className="mt-2 text-xs text-gray-600 flex items-center gap-1 truncate">
        {active.is_video ? <Play className="w-3 h-3 shrink-0" /> : <ImageIcon className="w-3 h-3 shrink-0" />}
        <span className="truncate">{active.ad_name || active.ad_id}</span>
        {active.status && active.status !== 'ACTIVE' && <span className="ml-1 text-[10px] text-gray-400">({active.status})</span>}
      </div>

      {ads.length > 1 && mode === 'media' && (
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          {ads.map((ad, i) => (
            <button
              key={ad.ad_id}
              onClick={() => setActiveIdx(i)}
              className={`relative shrink-0 w-14 h-14 rounded-lg border overflow-hidden ${i === activeIdx ? (color === 'blue' ? 'border-blue-500 ring-1 ring-blue-400' : 'border-orange-500 ring-1 ring-orange-400') : 'border-gray-200'}`}
            >
              {ad.image_url ? (
                <img src={ad.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-300"><ImageIcon className="w-4 h-4" /></div>
              )}
              {ad.is_video && <Play className="absolute bottom-0.5 right-0.5 w-3 h-3 text-white drop-shadow" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SideHeader({ side, name, color, winner }) {
  const ring = color === 'blue' ? 'border-blue-100 bg-blue-50/50' : 'border-orange-100 bg-orange-50/50';
  const text = color === 'blue' ? 'text-blue-700' : 'text-orange-700';
  return (
    <div className={`rounded-2xl border p-4 ${ring} relative`}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-bold uppercase ${text}`}>Campaign {side}</span>
        {winner && (
          <span className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
            <Trophy className="w-3 h-3" /> Winner
          </span>
        )}
      </div>
      <div className="text-sm font-mono text-gray-800 mt-1 break-all">{name || '—'}</div>
    </div>
  );
}
